import moment from 'moment'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import EditorFooter from '../../components/formComponents/editorFooter/EditorFooter'
import FormComponent from '../../components/formComponents/formComponent/FormComponent'
import LanguageSelector from '../../components/languageSelector/LanguageSelector'
import { GeneralTexts } from '../../language/Texts'
import { ComponentType } from '../../models/componentType.enum'
import { IComponent, IKeyValue } from '../../models/IComponent'
import { Utils } from '../../utils/Utils'
import WebView from '../webView/WebView'
import './EditorView.scss'
import i18n from '../../language/i18n'

function EditorView({ selectedLanguage, setSelectedLanguage, isSeasonEffect }) {
    const keyValuePair = Utils.getKeyValue()
    const getTemporaryManufacturerName = () => {
        if (type === ComponentType.GEMUE_INTERNAL) {
            return GeneralTexts.example;
        } else {
            return 'GEMÜ';
        }
    };

    const { type, backgroundColor, defaultProductName } = Utils.getDefaultParameters()
    const [manufacturerName, setManufacturerName] = useState<string>(getTemporaryManufacturerName())
    const [productName, setProductName] = useState<string>(defaultProductName)
    const [isAdvertisingAgreed, setIsAdvertisingAgreed] = useState(false)
    const [serialNumber, setSerialNumber] = useState<string>('123456789')
    const [materialNumber, setMaterialNumber] = useState<string>('987654321')
    const [productionDate, setProductionDate] = useState<string>(moment().format('YYYY-MM-DD'))
    const [productIcon, setProductIcon] = useState<{ iconFile: File, iconUrl: string }>({ iconFile: new File([], ''), iconUrl: '' })
    const [manufacturerIcon, setManufacturerIcon] = useState<{ iconFile: File, iconUrl: string }>({ iconFile: new File([], ''), iconUrl: '' })
    const [manufacturerColor, setManufacturerColor] = useState<string>(backgroundColor)
    const [manufacturerUrl, setManufacturerUrl] = useState<string>('gemu-group.com')
    const [keyValues, setKeyValues] = useState<IKeyValue[]>(keyValuePair)
    const [ownerEmail, setOwnerEmail] = useState<string>('')
    const [documents, setDocuments] = useState<File[]>([])
    const [windowWidth, setWindowWidth] = useState(0);
    const previewRef = useRef<HTMLDivElement>(null)
    const previewContentRef = useRef<HTMLDivElement>(null)
    const [previewLeft, setPreviewLeft] = useState<number | null>(null)
    const [previewWidth, setPreviewWidth] = useState<number | null>(null)


    const commonProps = {
        manufacturerName,
        setManufacturerName,
        productName,
        setProductName,
        serialNumber,
        setSerialNumber,
        materialNumber,
        setMaterialNumber,
        productionDate,
        setProductionDate,
        productIcon,
        setProductIcon,
        manufacturerIcon,
        setManufacturerIcon,
        manufacturerColor,
        setManufacturerColor,
        manufacturerUrl,
        setManufacturerUrl,
        keyValues,
        setKeyValues,
        ownerEmail,
        setOwnerEmail,
        documents,
        setDocuments,
        isAdvertisingAgreed,
        setIsAdvertisingAgreed,
        type,
        language: i18n.language
    };

    const component: IComponent = {
        manufacturerName: manufacturerName,
        productName: productName,
        serialNumber: serialNumber,
        materialNumber: materialNumber,
        productionDate: productionDate,
        productIcon: productIcon.iconUrl,
        manufacturerIcon: manufacturerIcon.iconUrl,
        manufacturerColor: manufacturerColor,
        manufacturerUrl: manufacturerUrl,
        keyValue: keyValues,
        ownerEmail: ownerEmail,
        document: documents,
        isAdvertisingAgreed: isAdvertisingAgreed,
        type: type
    }
    // Set title of the page
    document.title = productName

    // aspect ratio does not work on ios
    // That's why we use the height of the parent div
    const mobileDeviceBreakPoint = 769;
    const isMobile = window.innerWidth < mobileDeviceBreakPoint;
    let isIos = false;
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('mac') || userAgent.includes('iphone') || userAgent.includes('ipad')) {
        isIos = true;
    }

    const aspectRatio = 5 / 8; // Desired aspect ratio (width/height)
    const containerHeight = window.innerWidth * 0.45 / aspectRatio;


    let resizeWindow = () => {
        setWindowWidth(window.innerWidth);
    };

    const inlineStyleForIos = {
        height: isIos && !isMobile ? `${containerHeight}px` : 'auto',
        maxHeight: isIos && !isMobile ? '90vh' : 'auto'
    }

    useEffect(() => {
        resizeWindow();
        window.addEventListener("resize", resizeWindow);
        return () => window.removeEventListener("resize", resizeWindow);
    }, []);

    useLayoutEffect(() => {
        // Align the preview title with the dynamic WebView position on larger viewports
        if (isMobile) {
            setPreviewLeft(null);
            setPreviewWidth(null);
            return;
        }

        const currentPreview = previewRef.current;
        if (!currentPreview) {
            return;
        }

        const measure = () => {
            const contentElement = previewContentRef.current;
            const innerContainer = contentElement?.querySelector('.container_wrapper') as HTMLDivElement | null;
            const rectSource = innerContainer ?? contentElement ?? currentPreview;
            const boundingRect = rectSource.getBoundingClientRect();
            setPreviewLeft(boundingRect.left);
            setPreviewWidth(boundingRect.width);
        };

        measure();

        const resizeObserverTarget = previewContentRef.current ?? currentPreview;
        if (resizeObserverTarget && typeof ResizeObserver !== 'undefined') {
            const resizeObserver = new ResizeObserver(() => measure());
            resizeObserver.observe(resizeObserverTarget);
            return () => resizeObserver.disconnect();
        }
    }, [windowWidth, isMobile, type]);

    return (
        <div className={type !== ComponentType.GEMUE_EXTERNAL ? "editor_wrapper" : ""}>
            <div className="editor_wrapper_2">
                {type !== ComponentType.GEMUE_EXTERNAL && <div className="language_selector">
                    <LanguageSelector
                        selectedLanguage={selectedLanguage}
                        setSelectedLanguage={setSelectedLanguage}
                    />
                </div>}
                <div className="editor_content_wrapper">
                    <div className={type === ComponentType.GEMUE_EXTERNAL ? "gemue_editor_form_wrapper editor_form_wrapper" : "editor_form_wrapper"}>
                        {type === ComponentType.GEMUE_EXTERNAL && <div className='editor_form_title'>{GeneralTexts.productInformation}</div>}
                        <FormComponent {...commonProps} />
                    </div>
                    <div

                        className={type === ComponentType.GEMUE_EXTERNAL ? "gemue_editor_preview_wrapper editor_preview_wrapper" : "editor_preview_wrapper"}
                    >
                        {type === ComponentType.GEMUE_EXTERNAL && <div
                            className='editor_preview_title'
                            style={!isMobile && previewLeft !== null ? {
                                left: `${previewLeft - 19}px`,
                                width: previewWidth ? `${previewWidth}px` : undefined
                            } : undefined}
                        >{GeneralTexts.livePreview}</div>}
                        <div ref={previewRef} className={type === ComponentType.GEMUE_EXTERNAL ? "gemue_editor_preview editor_preview" : "editor_preview"}
                            style={inlineStyleForIos}>
                            <div ref={previewContentRef} className='editor_preview_content'>
                                <WebView
                                    isSeasonEffect={isSeasonEffect}
                                    component={component}
                                    isEditorView={true}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {commonProps.type !== ComponentType.GEMUE_EXTERNAL && <EditorFooter type={component.type} />}
            </div>
        </div>
    )
}

export default EditorView